import { useEffect, useState } from 'react';
import type { User } from 'oidc-client-ts';
import { hadPriorSession, markSessionPresence, safeReturnPath, userManager } from './authClient';
import { reportClientError } from '@peruselab/api-client/clientErrors';

export interface AuthState {
  readonly user: User | null;
  readonly isLoading: boolean;
  readonly signInError: string | null;
  /** #885: `returnTo` is the page to come back to after signing in (default: the home page). */
  readonly signIn: (returnTo?: string) => void;
  /** #889: create an account (Keycloak's sign-up page, OIDC `prompt=create`), then come back. */
  readonly signUp: (returnTo?: string) => void;
  /** Add another TOTP credential through Keycloak, preserving existing authenticators. */
  readonly addAuthenticator: (returnTo?: string) => void;
  readonly signOut: () => void;
}

export function useAuth(): AuthState {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [signInError, setSignInError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;

    // #531: the in-memory user store holds nothing across a reload, so a fresh page load almost
    // never finds an existing session via getUser() the way the old localStorage-backed store
    // did -- checked first anyway since it's a cheap in-memory read, and it's how a seeded e2e
    // session (tests/e2e/support/authFixture.ts) or a same-load remount still resolves instantly
    // without a network round trip. #544 made a same-origin silent restore (Keycloak's SSO
    // cookie, no visible redirect) work reliably for the real reload case, so that's the
    // fallback -- but only attempted when hadPriorSession() says one is actually worth trying, so
    // a browser that never signed in (or explicitly signed out) shows the sign-in screen
    // immediately instead of paying a network round trip or a silent-request timeout against an
    // unreachable authority.
    userManager
      .getUser()
      .then((current) => {
        if (!active) return;
        if (current && !current.expired) {
          setUser(current);
          setIsLoading(false);
          return;
        }
        if (hadPriorSession()) {
          userManager
            .signinSilent()
            .then((restored) => {
              if (active) {
                setUser(restored && !restored.expired ? restored : null);
                setIsLoading(false);
              }
            })
            .catch(() => {
              markSessionPresence(false);
              if (active) setIsLoading(false);
            });
        } else {
          setIsLoading(false);
        }
      })
      .catch(() => {
        if (active) setIsLoading(false);
      });

    const onUserLoaded = (loaded: User) => {
      markSessionPresence(true);
      setUser(loaded);
      setSignInError(null);
    };
    const onUserUnloaded = () => {
      markSessionPresence(false);
      setUser(null);
    };
    const onSessionEnded = () => {
      markSessionPresence(false);
      setUser(null);
      setSignInError('Your session has ended. Please sign in again.');
      // Clear the token used by API requests as well as this hook's UI state.
      void userManager.removeUser().catch(() => {});
    };
    const onSilentRenewError = () => {
      setSignInError('Your session could not be renewed. Please save your work and sign in again.');
    };
    userManager.events.addAccessTokenExpired(onSessionEnded);
    userManager.events.addUserSignedOut(onSessionEnded);
    userManager.events.addSilentRenewError(onSilentRenewError);
    userManager.events.addUserLoaded(onUserLoaded);
    userManager.events.addUserUnloaded(onUserUnloaded);
    return () => {
      active = false;
      userManager.events.removeAccessTokenExpired(onSessionEnded);
      userManager.events.removeUserSignedOut(onSessionEnded);
      userManager.events.removeSilentRenewError(onSilentRenewError);
      userManager.events.removeUserLoaded(onUserLoaded);
      userManager.events.removeUserUnloaded(onUserUnloaded);
    };
  }, []);

  // #885/#889: one redirect for sign-in and sign-up (`prompt: 'create'` opens Keycloak's sign-up
  // page); either way the visitor comes back to `returnTo`.
  const redirectToSignIn = (returnTo: string | undefined, prompt: 'create' | undefined, action?: 'CONFIGURE_TOTP') => {
    setSignInError(null);
    userManager
      .signinRedirect({
        state: { returnTo: safeReturnPath(returnTo) },
        ...(prompt ? { prompt } : {}),
        ...(action ? { max_age: 0, extraQueryParams: { kc_action: action } } : {})
      })
      .catch((error: unknown) => {
        // Reported to the server and logged locally for support/diagnostics; the on-screen
        // message stays generic so it doesn't expose the identity-provider setup to end users.
        const message = error instanceof Error ? error.message : String(error);
        console.error('[auth] sign-in failed:', error);
        reportClientError('sign_in_failed', message);
        setSignInError(
          'Sign-in is temporarily unavailable. Please try again shortly, or contact support if the problem continues.'
        );
      });
  };

  return {
    user,
    isLoading,
    signInError,
    signIn: (returnTo?: string) => redirectToSignIn(returnTo, undefined),
    signUp: (returnTo?: string) => redirectToSignIn(returnTo, 'create'),
    addAuthenticator: (returnTo?: string) => redirectToSignIn(returnTo, undefined, 'CONFIGURE_TOTP'),
    // Cleared explicitly rather than relying solely on the UserUnloaded event: the redirect this
    // triggers navigates the page away, and this marker must be gone before that happens, not
    // whenever oidc-client-ts's internal event ordering happens to get around to it.
    signOut: () => {
      markSessionPresence(false);
      void userManager.signoutRedirect();
    }
  };
}
